// src/api/wbuoaApi.js
import axios from 'axios';
import { getStoredToken } from '../utils/authStorage';

// Configure axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000',
  timeout: 30000, // 30 seconds timeout
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

// Request interceptor for adding auth token
api.interceptors.request.use(
  (config) => {
    const token = getStoredToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
api.interceptors.response.use(
  (response) => {
    // Handle successful responses
    return response.data;
  },
  (error) => {
    // Handle error responses
    if (error.response) {
      switch (error.response.status) {
        case 400:
          console.error('Bad Request:', error.response.data?.message);
          break;
        case 401:
          console.error('Unauthorized - Invalid or expired token');
          const requestUrl = error.config?.url || '';
          if (!requestUrl.includes('/users/login') && !requestUrl.includes('/users/updatepassword')) {
            window.dispatchEvent(new Event('unauthorized'));
          }
          break;
        case 403:
          console.error('Forbidden - Insufficient permissions');
          break;
        case 404:
          console.error('Endpoint not found:', error.config.url);
          break;
        case 500:
          console.error('Server Error:', error.response.data?.message);
          break;
        default:
          console.error('Request failed with status:', error.response.status);
      }
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Request setup error:', error.message);
    }

    return Promise.reject(error.response?.data || error.message);
  }
);

// Authentication & User Management Endpoints
export const authAPI = {
  login: (email, password) => api.post('/users/login', { email, password }),
  register: (userData) => api.post('/users', userData),
  getCurrentUser: () => api.get('/users/dashboard'),
  updateUser: (id, userData) => api.put(`/users/${id}`, userData),
  deleteUser: (id) => api.delete(`/users/${id}`),
  listUsers: () => api.get('/users'),

  updatePassword: (email, currentPassword, newPassword) => api.post('/users/updatepassword', {
    email,
    current_password: currentPassword,
    new_password: newPassword
  })
};
// Document Management Endpoints
export const documentAPI = {
  // List all document categories
  listCategories: () => api.get('/document-categories'),

  // Create a new category
  createCategory: (categoryData) => {
    // categoryData should be FormData with category_name and description
    if (categoryData instanceof FormData) {
      return api.post('/document-categories', categoryData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }
    // For JSON data
    return api.post('/document-categories', categoryData);
  },

  // Update a category
  updateCategory: (id, categoryData) => {
    if (categoryData instanceof FormData) {
      return api.put(`/document-categories/${id}`, categoryData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }
    return api.put(`/document-categories/${id}`, categoryData);
  },

  // Delete a category
  deleteCategory: (id) => api.delete(`/document-categories/${id}`),

  // List all documents
  listDocuments: () => api.get('/documents'),

  // Get a single document by ID
  getDocument: (id) => api.get(`/documents/${id}`),

  // Download document - using the correct endpoint from Postman
  downloadDocument: (id) => api.get(`/documents/${id}/file`, {
    responseType: 'blob'
  }),

  // Upload a new document
  uploadDocument: (formData) => {
    console.log('Uploading document with FormData:');
    for (let pair of formData.entries()) {
      console.log(pair[0] + ': ' + (pair[0] === 'file' ? 'File: ' + (pair[1]?.name || 'unknown') : pair[1]));
    }

    return api.post('/documents', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  // Update an existing document
  updateDocument: (id, formData) => {
    console.log('Updating document with FormData:');
    for (let pair of formData.entries()) {
      console.log(pair[0] + ': ' + (pair[0] === 'file' ? 'File: ' + (pair[1]?.name || 'unknown') : pair[1]));
    }

    return api.put(`/documents/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  // Delete a document
  deleteDocument: (id) => api.delete(`/documents/${id}`)
};

// University Management Endpoints
export const universityAPI = {
  listUniversities: (page = 1, filters = {}) => api.get('/universities', {
    params: { page, ...filters }
  }),
  listUniversitiesAll: () => api.get('/universities/all'),
  createUniversity: (universityData) => api.post('/universities', universityData),
  importUniversities: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/universities/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  updateUniversity: (id, universityData) => api.put(`/universities/${id}`, universityData),
  deleteUniversity: (id) => api.delete(`/universities/${id}`)
};

// Committee Management Endpoints
export const committeeAPI = {
  // Unit Committee
  listUnitCommittee: (page = 1, filters = {}) => api.get('/unitcommittee', {
    params: { page, ...filters }
  }),
  listAllUnitCommittee: () => api.get('/unitcommittee/all'),
  getUnitCommitteeById: (id) => api.get(`/unitcommittee/${id}`),
  createUnitMember: (memberData) => api.post('/unitcommittee', memberData),
  updateUnitMember: (id, memberData) => api.put(`/unitcommittee/${id}`, memberData),
  deleteUnitMember: (id) => api.delete(`/unitcommittee/${id}`),

  // State Committee
  listStateCommittee: (page = 1, filters = {}) => api.get('/statecommittee', {
    params: { page, ...filters }
  }),
  listStateCommitteeAll: () => api.get('/statecommittee/all'),
  getStateCommitteeById: (id) => api.get(`/statecommittee/${id}`),
  createStateMember: (memberData) => api.post('/statecommittee', memberData),
  updateStateMember: (id, memberData) => api.put(`/statecommittee/${id}`, memberData),
  deleteStateMember: (id) => api.delete(`/statecommittee/${id}`),

  // Executive Committee
  listExecutiveCommittee: (page = 1, filters = {}) => api.get('/executivecommittee', {
    params: { page, ...filters }
  }),
  listExecutiveCommitteeAll: () => api.get('/executivecommittee/all'),
  getExecutiveCommitteeById: (id) => api.get(`/executivecommittee/${id}`),
  createExecutiveCommittee: (memberData) => api.post('/executivecommittee', memberData),
  updateExecutiveCommittee: (id, memberData) => api.put(`/executivecommittee/${id}`, memberData),
  deleteExecutiveCommittee: (id) => api.delete(`/executivecommittee/${id}`),

  // Office Bearer
  listOfficeBearer: (page = 1, filters = {}) => api.get('/officebearer', {
    params: { page, ...filters }
  }),
  listOfficeBearerAll: () => api.get('/officebearer/all'),
  getOfficeBearerById: (id) => api.get(`/officebearer/${id}`),
  createOfficeBearer: (memberData) => api.post('/officebearer', memberData),
  updateOfficeBearer: (id, memberData) => api.put(`/officebearer/${id}`, memberData),
  deleteOfficeBearer: (id) => api.delete(`/officebearer/${id}`)
};

// President API
export const presidentAPI = {
  listPresidents: () => api.get('/president'),
  getPresident: (id) => api.get(`/president/${id}`),
  createPresident: (formData) => api.post('/president', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updatePresident: (id, data) => {
    if (data instanceof FormData) {
      return api.put(`/president/${id}`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }
    return api.put(`/president/${id}`, data);
  },
  deletePresident: (id) => api.delete(`/president/${id}`)
};

// Secretary API
export const secretaryAPI = {
  listSecretaries: () => api.get('/secretary'),
  getSecretary: (id) => api.get(`/secretary/${id}`),
  createSecretary: (formData) => api.post('/secretary', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateSecretary: (id, data) => {
    if (data instanceof FormData) {
      return api.post(`/secretary/${id}`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }
    return api.put(`/secretary/${id}`, data);
  },
  deleteSecretary: (id) => api.delete(`/secretary/${id}`)
};

// Gallery Management Endpoints
export const galleryAPI = {
  listGalleryItems: (filters = {}) => api.get('/gallery', { params: filters }),
  getGalleryItem: (id) => api.get(`/gallery/${id}`),
  getGalleryCategories: () => api.get('/gallery/categories'),
  getGalleryEvents: () => api.get('/gallery/events'),
  createGalleryItem: (formData) => {
    return api.post('/gallery', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  updateGalleryItem: (id, galleryData) => {
    if (galleryData instanceof FormData) {
      return api.put(`/gallery/${id}`, galleryData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }
    return api.put(`/gallery/${id}`, galleryData);
  },
  toggleArchiveStatus: (id, archive) => api.patch(`/gallery/${id}/archive`, { archive }),
  deleteGalleryItem: (id) => api.delete(`/gallery/${id}`)
};

// Test Endpoints
export const testAPI = {
  testBase: () => api.get('/'),
  testHello: (name) => api.get(`/hello/${name}`)
};

// News API functions with document upload support
export const newsAPI = {
  // List all news with pagination - FIXED to handle page parameter correctly
  listNews: (page = 1) => {
    console.log('Fetching news for page:', page);
    return api.get(`/news?page=${page}`);
  },

  // Get single news article
  getNews: (id) => api.get(`/news/${id}`),

  // Create new news article with optional document (FormData)
  createNews: (formData) => {
    console.log('Creating news with FormData');
    for (let pair of formData.entries()) {
      console.log(pair[0] + ': ' + (pair[0] === 'document_path' ? 'File: ' + (pair[1]?.name || 'unknown') : pair[1]));
    }
    return api.post('/news', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Update news article (JSON - for regular updates without document)
  updateNews: (id, data) => {
    console.log('Updating news with JSON:', id, data);
    return api.put(`/news/${id}`, data);
  },

  // Update news article with document (FormData)
  updateNewsWithDocument: (id, formData) => {
    console.log('Updating news with FormData:', id);
    for (let pair of formData.entries()) {
      console.log(pair[0] + ': ' + (pair[0] === 'document_path' ? 'File: ' + (pair[1]?.name || 'unknown') : pair[1]));
    }
    return api.put(`/news/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Partial update news article
  patchNews: (id, data) => {
    return api.patch(`/news/${id}`, data);
  },

  // Delete news article
  deleteNews: (id) => {
    console.log('Deleting news:', id);
    return api.delete(`/news/${id}`);
  },

  // Download document
  downloadDocument: async (id, documentPath) => {
    try {
      const response = await fetch(`${API_BASE_URL}/news/${id}/document`);
      if (!response.ok) {
        throw new Error('Failed to download document');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;

      const filename = documentPath ? documentPath.split('/').pop() : `news-document-${id}`;
      a.download = filename;

      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      return { success: true };
    } catch (error) {
      console.error('Download error:', error);
      throw error;
    }
  }
};

// Members API
export const membersAPI = {
  listMembers: () => api.get('/members'),
  getMember: (id) => api.get(`/members/${id}`),
  createMember: (formData) => {
    return api.post('/members', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  updateMember: (id, memberData) => {
    if (memberData instanceof FormData) {
      return api.put(`/members/${id}`, memberData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }
    return api.put(`/members/${id}`, memberData);
  },
  deleteMember: (id) => api.delete(`/members/${id}`)
};

// Default export for backward compatibility
export default api;
